import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Users,
  ClipboardList,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Database,
  Wifi,
  WifiOff,
  CalendarDays,
  TrendingUp,
  UserCheck,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/services/api";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

// ── helpers ──────────────────────────────────────────────────────────────────

const BAR_COLORS = [
  "#312e81", "#4338ca", "#6366f1", "#818cf8",
  "#a5b4fc", "#c7d2fe", "#e0e7ff", "#eef2ff",
  "#f1f5f9", "#e2e8f0",
];

const StatusDot = ({ ok }) =>
  ok ? (
    <span className="inline-flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
      Online
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-red-500 text-sm font-medium">
      <span className="w-2 h-2 rounded-full bg-red-500" />
      Offline
    </span>
  );

// Circular progress ring for approval rate
const ApprovalRing = ({ rate }) => {
  const r = 52;
  const circ = 2 * Math.PI * r;
  const dash = (rate / 100) * circ;
  return (
    <svg width="140" height="140" viewBox="0 0 140 140">
      <circle cx="70" cy="70" r={r} fill="none" stroke="#e2e8f0" strokeWidth="12" />
      <circle
        cx="70"
        cy="70"
        r={r}
        fill="none"
        stroke="#312e81"
        strokeWidth="12"
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeDashoffset={circ / 4}
        strokeLinecap="round"
        style={{ transition: "stroke-dasharray 0.8s ease" }}
      />
      <text x="70" y="66" textAnchor="middle" fontSize="22" fontWeight="700" fill="#1e293b">
        {rate}%
      </text>
      <text x="70" y="84" textAnchor="middle" fontSize="11" fill="#94a3b8">
        approved
      </text>
    </svg>
  );
};

// Custom tooltip for bar chart
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-sm">
        <p className="font-medium text-slate-800">{payload[0].payload.name}</p>
        <p className="text-indigo-700 font-semibold">{payload[0].value} registrants</p>
      </div>
    );
  }
  return null;
};

// ── main component ────────────────────────────────────────────────────────────

const AUTO_REFRESH_INTERVAL = 30; // seconds

export const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [countdown, setCountdown] = useState(AUTO_REFRESH_INTERVAL);

  const fetchDashboard = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const result = await api.dashboard.get();
      setData(result);
      setLastUpdated(new Date());
      setCountdown(AUTO_REFRESH_INTERVAL); // reset countdown after each fetch
    } catch (err) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboard(true);
    }, AUTO_REFRESH_INTERVAL * 1000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  // Countdown ticker
  useEffect(() => {
    const ticker = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? AUTO_REFRESH_INTERVAL : prev - 1));
    }, 1000);
    return () => clearInterval(ticker);
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const { kpi, type_breakdown, reviewer_activity, system } = data || {};

  const kpiCards = [
    {
      label: "Total Registrants",
      value: kpi?.total ?? 0,
      icon: ClipboardList,
      bg: "bg-indigo-50",
      iconColor: "text-indigo-950",
      badge: null,
    },
    {
      label: "Pending Review",
      value: kpi?.pending ?? 0,
      icon: Clock,
      bg: "bg-amber-50",
      iconColor: "text-amber-600",
      badge: kpi?.pending > 0 ? "Needs attention" : null,
      badgeColor: "bg-amber-100 text-amber-700",
    },
    {
      label: "Approved",
      value: kpi?.approved ?? 0,
      icon: CheckCircle2,
      bg: "bg-emerald-50",
      iconColor: "text-emerald-600",
      badge: null,
    },
    {
      label: "Declined",
      value: kpi?.declined ?? 0,
      icon: XCircle,
      bg: "bg-red-50",
      iconColor: "text-red-500",
      badge: null,
    },
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-dashboard">

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1
              className="text-2xl font-semibold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Admin Dashboard
            </h1>
            <p className="text-slate-500 mt-1">
              {system?.active_event
                ? `Active event: ${system.active_event}`
                : "Overview of your EventsAir registration pipeline"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Live indicator */}
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs text-slate-500">Live</span>
            </div>
            {/* Countdown ring */}
            <div className="flex items-center gap-1.5" title={`Auto-refreshes in ${countdown}s`}>
              <svg width="28" height="28" viewBox="0 0 28 28" className="-rotate-90">
                <circle cx="14" cy="14" r="11" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                <circle
                  cx="14" cy="14" r="11" fill="none"
                  stroke="#6366f1" strokeWidth="3"
                  strokeDasharray={`${(countdown / AUTO_REFRESH_INTERVAL) * 69.1} 69.1`}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dasharray 0.9s linear" }}
                />
              </svg>
              <span className="text-xs text-slate-500 tabular-nums w-5">{countdown}s</span>
            </div>
            {lastUpdated && (
              <span className="text-xs text-slate-400 hidden sm:inline">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchDashboard(true); setCountdown(AUTO_REFRESH_INTERVAL); }}
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* ── Section 1: KPI Cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((card, i) => (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-lg ${card.bg}`}>
                  <card.icon className={`h-5 w-5 ${card.iconColor}`} strokeWidth={1.5} />
                </div>
                {card.badge && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${card.badgeColor}`}>
                    {card.badge}
                  </span>
                )}
              </div>
              <div>
                <p
                  className="text-3xl font-bold text-slate-900"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {card.value.toLocaleString()}
                </p>
                <p className="text-sm text-slate-500 mt-0.5">{card.label}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── Section 2 + 3: Chart + Approval Ring ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Bar chart — 2/3 width */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6"
          >
            <div className="flex items-center gap-2 mb-5">
              <TrendingUp className="h-5 w-5 text-indigo-950" strokeWidth={1.5} />
              <h2
                className="text-base font-semibold text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Registrants by Type
              </h2>
            </div>
            {type_breakdown && type_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={type_breakdown}
                  layout="vertical"
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                >
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={170}
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={22}>
                    {type_breakdown.map((_, index) => (
                      <Cell key={index} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
                No registration data available
              </div>
            )}
          </motion.div>

          {/* Approval rate ring — 1/3 width */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col items-center justify-center gap-4"
          >
            <div className="flex items-center gap-2 self-start">
              <CheckCircle2 className="h-5 w-5 text-indigo-950" strokeWidth={1.5} />
              <h2
                className="text-base font-semibold text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Approval Rate
              </h2>
            </div>
            <ApprovalRing rate={kpi?.approval_rate ?? 0} />
            <div className="w-full space-y-2 text-sm">
              <div className="flex justify-between text-slate-600">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-950 inline-block" />
                  Approved
                </span>
                <span className="font-semibold">{kpi?.approved ?? 0}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-200 inline-block" />
                  Remaining
                </span>
                <span className="font-semibold">{(kpi?.total ?? 0) - (kpi?.approved ?? 0)}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Section 4: Reviewer Activity ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl border border-slate-200"
        >
          <div className="p-6 border-b border-slate-100 flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-indigo-950" strokeWidth={1.5} />
            <h2
              className="text-base font-semibold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Reviewer Activity
            </h2>
          </div>
          {reviewer_activity && reviewer_activity.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Reviewer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Assigned Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Organization</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Reviewed</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-emerald-600 uppercase tracking-wider">Approved</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-red-500 uppercase tracking-wider">Declined</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 uppercase tracking-wider">Pending</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {reviewer_activity.map((rev) => (
                    <tr key={rev.id}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <Users className="h-4 w-4 text-slate-500" strokeWidth={1.5} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{rev.full_name}</p>
                            <p className="text-xs text-slate-400 truncate">{rev.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        <Badge variant="outline" className="text-xs text-slate-600 border-slate-200">
                          {rev.assigned_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        {rev.assigned_organization ? (
                          <Badge className="text-xs bg-violet-50 text-violet-700 border-violet-200 font-normal">
                            {rev.assigned_organization}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400">All orgs</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span
                          className="text-base font-bold text-slate-700"
                          style={{ fontFamily: "Manrope, sans-serif" }}
                        >
                          {rev.reviewed_count ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span
                          className={`text-base font-bold ${
                            (rev.approved_count ?? 0) > 0 ? "text-emerald-600" : "text-slate-400"
                          }`}
                          style={{ fontFamily: "Manrope, sans-serif" }}
                        >
                          {rev.approved_count ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span
                          className={`text-base font-bold ${
                            (rev.declined_count ?? 0) > 0 ? "text-red-500" : "text-slate-400"
                          }`}
                          style={{ fontFamily: "Manrope, sans-serif" }}
                        >
                          {rev.declined_count ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span
                          className={`text-base font-bold ${
                            (rev.pending_count ?? 0) > 0 ? "text-amber-600" : "text-emerald-600"
                          }`}
                          style={{ fontFamily: "Manrope, sans-serif" }}
                        >
                          {rev.pending_count ?? 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-10 text-center text-slate-400 text-sm">
              No reviewers configured yet. Add reviewers in User Management.
            </div>
          )}
        </motion.div>

        {/* ── Section 5: System Health Strip ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl border border-slate-200 p-5"
        >
          <h2
            className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            System Health
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-slate-500 text-xs">
                <Database className="h-3.5 w-3.5" />
                Database
              </div>
              <StatusDot ok={system?.mongodb_connected} />
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-slate-500 text-xs">
                {system?.redis_connected ? (
                  <Wifi className="h-3.5 w-3.5" />
                ) : (
                  <WifiOff className="h-3.5 w-3.5" />
                )}
                Cache (Redis)
              </div>
              <StatusDot ok={system?.redis_connected} />
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-slate-500 text-xs">
                <CheckCircle2 className="h-3.5 w-3.5" />
                EventsAir API
              </div>
              <StatusDot ok={system?.eventsair_token_valid} />
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-slate-500 text-xs">
                <CalendarDays className="h-3.5 w-3.5" />
                Active Event
              </div>
              <span className="text-sm font-medium text-slate-700 truncate">
                {system?.active_event || "—"}
              </span>
            </div>
          </div>
        </motion.div>

      </div>
    </Layout>
  );
};
